import { useEffect, useState } from 'react';
import { resourcesAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  BookOpen, PlayCircle, FileText, HelpCircle, Wrench, ShieldCheck,
  Settings, ExternalLink, Search, Phone, LifeBuoy
} from 'lucide-react';

const SUPPORT_EMAIL = 'support@chinookmanufacturing.com';
const SUPPORT_PHONE = '1-587-388-0555';

const CATEGORY_META = {
  user_manual: { label: 'User Manuals', icon: BookOpen },
  installation_guide: { label: 'Installation Guides', icon: Settings },
  troubleshooting: { label: 'Troubleshooting', icon: Wrench },
  warranty_terms: { label: 'Warranty Terms', icon: ShieldCheck },
  safety_info: { label: 'Safety Information', icon: ShieldCheck },
  specifications: { label: 'Specifications', icon: FileText },
  faq: { label: 'FAQ', icon: HelpCircle },
  video_tutorial: { label: 'Video Tutorials', icon: PlayCircle },
  other: { label: 'More Tools & Resources', icon: FileText },
};

const CustomerPortalResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await resourcesAPI.getAll({ is_active: true });
        setResources(res.data || []);
      } catch (error) {
        toast.error('Failed to load resources');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = resources.filter(r => {
    const q = search.toLowerCase();
    return !q || r.title?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
  });

  const grouped = filtered.reduce((acc, r) => {
    const cat = r.category || 'other';
    (acc[cat] = acc[cat] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="customer-portal-resources">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-blue-600" />
          Resources &amp; Support
        </h1>
        <p className="text-muted-foreground mt-1">
          Helpful videos, guides, FAQs, and tools for your freeze dryer.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search resources..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="resources-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No resources available yet. Check back soon.</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([cat, items]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.other;
          const Icon = meta.icon;
          return (
            <div key={cat} data-testid={`resource-category-${cat}`}>
              <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
                <Icon className="h-5 w-5 text-blue-600" />
                {meta.label}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map(r => (
                  <Card key={r.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        {cat === 'video_tutorial' && <PlayCircle className="h-4 w-4 text-red-500" />}
                        {r.title}
                      </CardTitle>
                      {r.description && <CardDescription>{r.description}</CardDescription>}
                    </CardHeader>
                    <CardContent>
                      {r.content && (
                        <div className="text-sm text-slate-600 mb-3" dangerouslySetInnerHTML={{ __html: r.content }} />
                      )}
                      {r.file_url && (
                        <a
                          href={r.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          data-testid={`resource-link-${r.id}`}
                        >
                          {cat === 'video_tutorial' ? 'Watch video' : 'Open resource'}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Still need help */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-blue-900">Still need help?</p>
            <p className="text-sm text-blue-700">Our support team is here for you.</p>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1 text-blue-700 font-medium hover:underline">
              <Phone className="h-4 w-4" /> {SUPPORT_PHONE}
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1 text-blue-700 font-medium hover:underline">
              <ExternalLink className="h-4 w-4" /> {SUPPORT_EMAIL}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerPortalResourcesPage;
